import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AccountSettingsDto,
  GetAccountSettingsResponseDto,
  UpdateAccountSettingsDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

export const useAccountSettingsApi = (api: AxiosInstance) => {
  const baseUrl = `/account-settings`;

  const getAccountSettings = async (): Promise<GetAccountSettingsResponseDto> => {
    const { data } = await api.get(baseUrl);
    return data;
  };

  const queryClient = useQueryClient();
  const getAccountSettingsQueryKey = 'account-settings';

  const updateAccountSettings = async (
    updatedAccountSettings: UpdateAccountSettingsDto,
  ): Promise<{ data: AccountSettingsDto }> => {
    const { data } = await api.put(`${baseUrl}`, {
      ...updatedAccountSettings,
    });

    return { data };
  };

  const useGetAll = () =>
    useQuery({
      queryKey: [getAccountSettingsQueryKey],
      queryFn: getAccountSettings,
    });

  const useUpdate = () =>
    useErrorHandlingMutation({
      mutationFn: updateAccountSettings,
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [getAccountSettingsQueryKey],
        }),
    });

  const assignFramework = async (id: number): Promise<GetAccountSettingsResponseDto> => {
    const { data } = await api.post(`${baseUrl}/priority-frameworks/${id}/assign`);
    return data;
  };

  const unassignFramework = async (id: number): Promise<GetAccountSettingsResponseDto> => {
    const { data } = await api.post(`${baseUrl}/priority-frameworks/${id}/unassign`);
    return data;
  };

  const useAssignFramework = () =>
    useErrorHandlingMutation({
      mutationFn: assignFramework,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getAccountSettingsQueryKey],
        });
        queryClient.invalidateQueries({
          queryKey: ['/priority-frameworks'],
        });
      },
    });

  const useUnassignFramework = () =>
    useErrorHandlingMutation({
      mutationFn: unassignFramework,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getAccountSettingsQueryKey],
        });
        queryClient.invalidateQueries({
          queryKey: ['/priority-frameworks'],
        });
      },
    });

  return {
    useGetAll,
    useUpdate,
    useAssignFramework,
    useUnassignFramework,
  };
};
